import { extend, uid } from "@dhx/ts-common/core";
import { toNode } from "@dhx/ts-common/html";
import { Id } from "@dhx/ts-common/types";
import { DataCollection } from "@dhx/ts-data";
import {
	FileStatus,
	IFileWrapper,
	IParams,
	IUploader,
	IUploaderConfig,
	IUploaderEventSystem,
	IVaultEventSystem,
	UploaderEvents,
} from "./types";

export class Uploader implements IUploader {
	public config: IUploaderConfig;
	public data: DataCollection<IFileWrapper>;
	public events: IUploaderEventSystem;
	public isActive: boolean;

	private _fileInput: HTMLInputElement;
	private _dropAreas: Map<HTMLElement, { drop: (e) => void; dragover: (e) => void }>;
	private _uploadInfo: {
		files: IFileWrapper[];
		count: number;
		size: number;
	};

	constructor(
		config: IUploaderConfig = {},
		data?: DataCollection<IFileWrapper>,
		events?: IVaultEventSystem
	) {
		this.config = extend(
			{
				autosend: true,
				updateFromResponse: true,
				fieldName: "file",
			},
			config
		);
		this.data = data || new DataCollection<IFileWrapper>();
		this.events = events || (this.data.events as IVaultEventSystem);

		this.isActive = false;

		this._fileInput = document.createElement("input");
		this._fileInput.type = "file";
		this._fileInput.multiple = true;
		this._initEvents();
		this._dropAreas = new Map();
	}

	selectFile(): void {
		this._fileInput.click();
	}

	linkDropArea(element: HTMLElement | string): void {
		const node = toNode(element);
		const dragover = e => e.preventDefault();
		const drop = e => {
			e.preventDefault();

			this.parseFiles(e.dataTransfer);
		};
		node.addEventListener("dragover", dragover);
		node.addEventListener("drop", drop);
		this._dropAreas.set(node, {
			dragover,
			drop,
		});
	}

	unlinkDropArea(element?: HTMLElement | string) {
		if (!element) {
			this._dropAreas.forEach((_, node) => {
				this._unlinkDropArea(node);
			});
			this._dropAreas.clear();
		} else {
			const node = toNode(element);
			this._unlinkDropArea(node);
			this._dropAreas.delete(node);
		}
	}

	parseFiles(dataTransfer: DataTransfer) {
		if (!dataTransfer.items || !dataTransfer.items[0] || !dataTransfer.items[0].webkitGetAsEntry) {
			const files = dataTransfer.files;
			for (let i = 0; i < files.length; i++) {
				this._addFile(files[i]);
			}
			if (this.config.autosend) {
				this.send();
			}
		} else {
			this._parseAsWebkitEntry(dataTransfer.items);
		}
	}

	send(params?: IParams): void {
		if (this._uploadInfo && this.isActive) {
			// cancel two active sends
			return;
		}
		const all = this.data.findAll(
			item => item.status === FileStatus.queue || item.status === FileStatus.failed
		);
		const files = all.filter(file => this.events.fire(UploaderEvents.beforeUploadFile, [file]));
		if (!files.length) {
			return;
		}

		this.isActive = true;
		this._uploadInfo = {
			files,
			count: files.length,
			size: files.reduce((s, f) => s + f.file.size, 0),
		};

		this.events.fire(UploaderEvents.uploadBegin, [files]);
		this.events.fire(UploaderEvents.uploadProgress, [0, 0, this._uploadInfo.size]);

		if (this.config.singleRequest) {
			this._xhrSend(files, params);
		} else {
			for (const fileWrapper of files) {
				this._xhrSend([fileWrapper], params);
			}
		}
	}
	abort(id?: Id) {
		if (!id) {
			if (!this._uploadInfo || !this._uploadInfo.files) {
				return;
			}
			for (const fileWrapper of this._uploadInfo.files) {
				this.abort(fileWrapper.id);
			}
			return;
		} else {
			const item = this.data.getItem(id);
			if (!item || !item.request || item.request.readyState === 4) {
				return;
			}
			item.request.abort();
		}
	}

	private _unlinkDropArea(node) {
		const handlers = this._dropAreas.get(node);
		if (!handlers) {
			return;
		}

		const { dragover, drop } = handlers;
		node.removeEventListener("dragover", dragover);
		node.removeEventListener("drop", drop);
	}

	private _initEvents(): void {
		this._fileInput.addEventListener("change", () => {
			const files = this._fileInput.files;
			for (let i = 0; i < files.length; i++) {
				this._addFile(files[i]);
			}
			if (this.config.autosend) {
				this.send();
			}
			this._fileInput.value = null; // clear file input after get info about files
		});
	}

	private _xhrSend(fileWrappers: IFileWrapper[], params?: IParams): void {
		const formData = this._createFormData(fileWrappers, params);
		const request = new XMLHttpRequest();
		const headerParams = this.config.headerParams;

		for (const fileWrapper of fileWrappers) {
			this.data.update(fileWrapper.id, {
				request,
				status: FileStatus.inprogress,
				progress: 0,
			});
		}

		request.open("POST", this.config.target);
		if (headerParams) {
			for (const headerItem in headerParams) {
				request.setRequestHeader(headerItem, headerParams[headerItem]);
			}
		}
		request.upload.onprogress = ev => {
			for (const fileWrapper of fileWrappers) {
				this.data.update(fileWrapper.id, {
					progress: ev.loaded / ev.total,
					status: FileStatus.inprogress,
				});
			}

			const current =
				this._uploadInfo.files.reduce((tot, file) => tot + file.size * file.progress, 0) || 0;
			const total = this._uploadInfo.size;
			const progress = (current / this._uploadInfo.size) * 100 || 0;

			this.events.fire(UploaderEvents.uploadProgress, [progress, current, total]);
		};
		request.onloadend = () => {
			this._uploadInfo.count = this.config.singleRequest ? 0 : this._uploadInfo.count - 1;

			const status = request.status === 200 ? FileStatus.uploaded : FileStatus.failed;
			const extra = request.status === 200 && request.response ? JSON.parse(request.response) : null;

			for (const fileWrapper of fileWrappers) {
				this.data.update(fileWrapper.id, { status });
				if (status === FileStatus.uploaded) {
					if (this.config.updateFromResponse && extra) {
						if (this.config.singleRequest && extra[fileWrapper.id]) {
							this.data.update(fileWrapper.id, extra[fileWrapper.id]);
						} else if (!this.config.singleRequest) {
							this.data.update(fileWrapper.id, extra);
						}
					}
					this.events.fire(UploaderEvents.uploadFile, [fileWrapper, extra]);
				} else {
					this.events.fire(UploaderEvents.uploadFail, [fileWrapper]);
				}
			}

			if (this._uploadInfo.count === 0) {
				this.isActive = false;
				this.events.fire(UploaderEvents.uploadComplete, [this._uploadInfo.files]);
			}
		};
		request.send(formData);
	}

	private _parseAsWebkitEntry(items) {
		const reads = [];
		for (let i = 0; i < items.length; i++) {
			const item = items[i].webkitGetAsEntry();
			reads.push(this._traverseFileTree(item));
		}

		Promise.all(reads).then(() => {
			if (this.config.autosend) {
				this.send();
			}
		});
	}

	private _createFormData(fileWrappers: IFileWrapper[], params?: IParams): FormData {
		const fieldName = this.config.fieldName;
		const formData = new FormData();
		const extraParams = this.config.params;

		if (params) {
			for (const key in params) {
				formData.append(key, params[key]);
			}
		}
		if (extraParams) {
			for (const key in extraParams) {
				formData.append(key, extraParams[key]);
			}
		}
		const brackets = fileWrappers.length > 1 ? "[]" : "";
		for (const fileWrapper of fileWrappers) {
			formData.append(fieldName + brackets, fileWrapper.file, fileWrapper.file.name);
			formData.append(fieldName + "_fullname" + brackets, fileWrapper.path + fileWrapper.file.name);
			const id = typeof fileWrapper.id === "object" ? fileWrapper.id : fileWrapper.id.toString();
			formData.append(fieldName + "_id" + brackets, id);
		}
		return formData;
	}

	private _addFile(file: File, path = ""): void {
		const fileWrapper: IFileWrapper = {
			id: uid(),
			file,
			progress: 0,
			status: FileStatus.queue,
			src: null,
			path,
		};

		this.data.add(fileWrapper);
	}

	private _traverseFileTree(item) {
		return new Promise<void>(res => {
			let count = 0;
			const readDirectory = (reader, path) => {
				count++;

				reader.readEntries(entries => {
					count--;

					for (const entry of entries) {
						// eslint-disable-next-line @typescript-eslint/no-use-before-define
						readEntry(entry, path);
					}

					if (count === 0) {
						res();
					}
				});
			};
			const readEntry = (entry, path) => {
				if (entry.isFile) {
					count++;

					entry.file(file => {
						count--;
						this._addFile(file, path);

						if (count === 0) {
							res();
						}
					});
				} else if (entry.isDirectory) {
					const reader = entry.createReader();
					readDirectory(reader, path + entry.name + "/");
				}
			};

			readEntry(item, "");
		});
	}
}
